import { PmiButton } from 'shell/components/shared/button';
import { useFormik } from 'formik';
import { DateTime } from 'luxon';
import { ResidenceID } from 'practice/constants/id-practice-edit-residence';
import { TypeRegister } from 'practice/constants/type-register';
import { memo, ReactElement, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponseList } from 'shell/models/http-response';
import { PracticeFamily } from 'shell/models/practice-family';
import AccordionFamily from './family';
import { FamilyVM } from './family/table-family';
import ModalDomesticPartnership from './modal-domestic-partnership';
import AccordionPerson from './person';
import { PersonFormVM } from './person/form';
import { SubjectData } from 'shell/models/person';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { NewPersonVM } from 'shell/components/shared/sipo-person-form/new-person';
import { transformGenerality } from 'shell/utils/consult-person/response-transformer/registry-office/generality';
import ErrorModal from 'shell/components/shared/modal-error';
import { ErrorModalVM } from 'shell/view-models/error-modal';
import {
  transformCitizenship,
  transformGenerality as transformGeneralityRequest,
} from 'practice/utils/transform-request-step-register-office';
import { useEventCallback } from '@mui/material';
import { blobToBase64 } from 'shell/utils/base64-to-blob';
import PrintSuccessModalDeclarationDeclarant from '../step-register-office/print-success-modal-declaration-declarant';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import { RegisterOfficeVM } from 'shell/view-models/register-office-practice-vm';
import { PracticeDraftBEStep, TypeInitiative } from 'practice/constants/practice-management-step';

interface StepFamilyProps {
  onBack: () => void;
  onNext: () => void;
  codeEnte?: string;
  yearId?: string;
  typeRegister?: string;
  progressive?: string;
  typePractice?: string;
  codOperatorePratica?: string;
  idFamily?: string;
  maxActiveStep: number;
  disabledAll?: boolean;
  iniziativa?: string;
  ForPracticeTemporary?: boolean;
  stepRegisterOfficeState?: RegisterOfficeVM;
}

const StepFamilyComponent = ({
  codeEnte,
  yearId,
  typeRegister,
  progressive,
  typePractice,
  idFamily,
  codOperatorePratica,
  onBack,
  onNext,
  maxActiveStep,
  disabledAll,
  ForPracticeTemporary,
  iniziativa,
  stepRegisterOfficeState,
}: StepFamilyProps): ReactElement => {
  const filterTable = useFilterTable<NewPersonVM>();

  const isPraticeUnAvailability: boolean = useMemo(() => {
    return (
      typeRegister === TypeRegister.Unavailable || typeRegister === TypeRegister.UntraceableCensus
    );
  }, [typeRegister]);

  const [openModal, setOpenModal] = useState<boolean>(false);

  const [errorModal, setErrorModal] = useState<ErrorModalVM>({ open: false });

  const [familyMembers] = useHttpFetch<HttpResponseList<PracticeFamily>, Array<FamilyVM>>(
    `${__PRACTICE_API_URL__}/visualizzazione-scelta-famiglia`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          key: {
            tipoRegistro: typeRegister,
            idAnnoPratica: yearId ? Number.parseInt(yearId, 10) : undefined,
            progressivoPratica: progressive ? Number.parseInt(progressive, 10) : undefined,
          },
        },
      },
      transformer: (response) => {
        return response.data.payload?.list
          ?.filter((item) => item?.posizionePersona !== 'X')
          ?.map((subject) => ({
            surname: subject.generalita?.cognome || '',
            name: subject.generalita?.nome || '',
            fiscalCode: subject.generalita?.codiceFiscale?.codFiscale || '',
            birthDate: subject.generalita?.dataNascita
              ? DateTime.fromISO(subject.generalita?.dataNascita).toFormat('dd/MM/yyyy')
              : '',
            relationship: subject.legameSoggetto?.codiceLegame || '',
            oldRelationship:
              subject.vecchioCodiceLegame || subject.legameSoggetto?.codiceLegame || '',
            birthplace:
              (subject.generalita?.luogoNascita?.localita
                ? subject.generalita?.luogoNascita?.localita?.descrizioneStato
                : subject.generalita?.luogoNascita?.comune?.nomeComune) || '',
            residence: subject.indirizzo || '',
            drivingLicense: subject.autoveicoli?.possessoPatente === 'S' ? 'S' : 'N',
            auto: subject.autoveicoli?.possessoAutoveicoli === 'S' ? 'S' : 'N',
            aire: subject.generalita?.soggettoAIRE || 'N',
            cfControl: subject.controlloCf || '1',
            subject,
            selectedFromDb: (subject.selected && subject.intestatario) || false,
            checked: subject.selected || false,
            idAnprIntestatario: subject.idAnprIntestatario || '',
            intestatario: subject.intestatario || false,
            richiedente: subject.flagRichiedente || false,
            isPersonaNonResidente: subject.personaNonResidente === '1',
            isMaiAnagrafato: subject.personaNonResidente === '2',
            tipoCasoPratica: subject.tipoCasoPratica?.toString() || '',
            tipoLegame: subject.legameSoggetto?.tipoLegame || '',
            idSchedaSoggettoANPR: subject.generalita?.idSchedaSoggettoANPR || '',
            idIntestatario: subject.generalita?.idSchedaSoggettoComune?.idSchedaSoggetto || '',
            isEditGenerality:
              subject.personaNonResidente === '1' || subject.personaNonResidente === '2', // se irriperibili o mai anagrafato
            generality: subject.generalita
              ? transformGenerality(subject as SubjectData)
              : undefined,
          }));
      },
    }
  );

  const data = (familyMembers.data as Array<FamilyVM>) || [];

  const isPersonaNonResidente = useMemo(() => {
    return data.filter((item) => item.isPersonaNonResidente || item.isMaiAnagrafato).length > 0;
  }, [data]);

  const isMaiAnagrafato = useMemo(() => {
    return data.filter((item) => item.isMaiAnagrafato).length > 0;
  }, [data]);

  const idAnprIntestatario = useMemo(() => {
    if (data.length === 0) {
      return false;
    }

    return data.find((item) => item.intestatario)?.idAnprIntestatario !== '0' ? true : false;
  }, [data]);

  const saveData = useHttpClient(
    `${__PRACTICE_API_URL__}/visualizzazione-scelta-famiglia/inserisci`,
    {
      options: {
        method: 'POST',
      },
    }
  );

  const formContext = useFormik<{
    family: Array<FamilyVM>;
    person?: PersonFormVM;
  }>({
    initialValues: { family: data },
    onSubmit: (values, context) => {
      if (disabledAll) {
        onNext();
        return;
      }

      let familyStepFamily = [] as Array<PracticeFamily>;
      let familyStepPerson = [] as Array<PracticeFamily>;

      const familyValue = values.family || [];

      const familySelected =
        (values.family || []).filter((item) => item.checked || item.selectedFromDb) || [];

      const selectedAll = familyValue.length === familySelected.length;

      const hasChangedParenthood = familySelected.some((memberFamily) => {
        let hasChanged = false;

        formContext.initialValues.family.forEach((initialValueMember) => {
          if (
            initialValueMember.relationship !== memberFamily.relationship &&
            initialValueMember.fiscalCode === memberFamily.fiscalCode &&
            initialValueMember.idSchedaSoggettoANPR === memberFamily.idSchedaSoggettoANPR &&
            initialValueMember.idIntestatario === memberFamily.idIntestatario
          ) {
            hasChanged = true;
          }
        });

        return hasChanged;
      });

      if (
        selectedAll &&
        (typeRegister === TypeRegister.Residence || TypeRegister.Immigration) &&
        familyValue.length > 1 &&
        hasChangedParenthood
      ) {
        setErrorModal({
          open: true,
          message:
            'Per cambio residenza intero nucleo o immigrazione intero nucleo non è possibile modificare le parentele',
        });

        context.setFieldValue(
          'family',
          familyValue.map((el) => {
            let member = el;
            formContext.initialValues.family.forEach((initialValueMember) => {
              if (
                initialValueMember.fiscalCode === el.fiscalCode &&
                initialValueMember.idSchedaSoggettoANPR === el.idSchedaSoggettoANPR &&
                initialValueMember.idIntestatario === el.idIntestatario
              ) {
                member = {
                  ...member,
                  relationship: initialValueMember.relationship,
                };
              }
            });
            return member;
          })
        );

        return;
      }

      if (values.family) {
        familyStepFamily = (values.family || []).map((item) => ({
          ...(item.subject as PracticeFamily),
          controlloCf: item.cfControl,
          // controllare se cittadinanza è un array
          cittadinanza:
            ((item?.subject as SubjectData)?.cittadinanza || [])?.length > 0
              ? ((item?.subject as SubjectData)?.cittadinanza || [])[0]
              : ((item?.subject as SubjectData)?.cittadinanza || [])?.length === 0
              ? undefined
              : (item.subject as PracticeFamily)?.cittadinanza || undefined,
          autoveicoli: {
            possessoAutoveicoli: item.auto,
            possessoPatente: item.drivingLicense,
          },
          generalita: {
            ...item.subject?.generalita,
            soggettoAIRE: item.aire,
          },
          legameSoggetto: {
            ...item.subject?.legameSoggetto,
            codiceLegame: item.relationship,
          },
          vecchioCodiceLegame: item.oldRelationship,
          selected: item.checked || item.selectedFromDb || false,
        })) as Array<PracticeFamily>;
      }

      if (values.person?.family) {
        familyStepPerson = (values.person?.family || []).map((item) => ({
          ...(item.subject as PracticeFamily),
          controlloCf: item.cfControl,
          // controllare se cittadinanza è un array
          cittadinanza:
            ((item?.subject as SubjectData)?.cittadinanza || [])?.length > 0
              ? ((item?.subject as SubjectData)?.cittadinanza || [])[0]
              : ((item?.subject as SubjectData)?.cittadinanza || [])?.length === 0
              ? undefined
              : (item.subject as PracticeFamily)?.cittadinanza || undefined,
          autoveicoli: {
            possessoAutoveicoli: item.auto,
            possessoPatente: item.drivingLicense,
          },
          generalita: {
            ...item.subject?.generalita,
            soggettoAIRE: item.aire,
          },
          legameSoggetto: {
            ...item.subject?.legameSoggetto,
            codiceLegame: item.relationship,
          },
          vecchioCodiceLegame: item.oldRelationship,
          selected: true,
        })) as Array<PracticeFamily>;
      }

      const dataRequest = {
        tipoRegistro: typeRegister || undefined,
        idAnnoPratica: yearId ? Number.parseInt(yearId, 10) : undefined,
        progressivoPratica: progressive ? Number.parseInt(progressive, 10) : undefined,
        selectedAll,
        visualizzazioneSceltaFamigliaList: (familyStepPerson || []).concat(familyStepFamily || []),
        stepBozza:
          maxActiveStep > PracticeDraftBEStep.Family ? maxActiveStep : PracticeDraftBEStep.Family,
      };

      (saveData({ data: dataRequest }) as ResponsePromise<void>).then(() => {
        toast.success('Operazione eseguita con successo');
        // reset new person
        filterTable.setValues(undefined);

        onNext();
      });
    },
    enableReinitialize: true,
  });

  const disabled = useMemo(() => {
    if (disabledAll) {
      return true;
    }

    // if (iniziativa === TypeInitiative.Anpr || iniziativa === TypeInitiative.ElixForm) {
    //   // ANPR o ELIXFORM
    //   return true;
    // }

    // if (codOperatorePratica === 'BATCH') {
    //   return true;
    // }

    // if (maxActiveStep >= 2) {
    //   return true;
    // }

    return false;
  }, [codOperatorePratica, disabledAll, iniziativa, maxActiveStep]);

  // Spostato bottone stampa dichiarante
  const [pdfModal, setPdfModal] = useState({ data: '', open: false, name: '' });

  const [isDeclarationSuccessModal, setDeclarationSuccessModal] = useState<boolean>(false);
  const [disabledPrintDeclaration, setDisabledPrintDeclaration] = useState<boolean>(true);

  const declarationDeclarant = useHttpClient<Blob>('', {
    options: { method: 'POST' },
  });

  const printDeclarationDeclarant = useEventCallback(() => {
    let luogoRilascioDocumento = undefined;

    if (
      stepRegisterOfficeState?.applicantData?.identificationDocument?.municipality?.description &&
      !stepRegisterOfficeState?.applicantData?.identificationDocument?.isForeign
    ) {
      luogoRilascioDocumento =
        stepRegisterOfficeState?.applicantData?.identificationDocument?.municipality?.description;
    } else if (
      stepRegisterOfficeState?.applicantData?.identificationDocument?.placeForeign &&
      stepRegisterOfficeState?.applicantData?.identificationDocument?.isForeign
    ) {
      luogoRilascioDocumento =
        stepRegisterOfficeState?.applicantData?.identificationDocument?.placeForeign;
    }

    if (
      stepRegisterOfficeState?.accountholderData?.identificationDocument?.municipality
        ?.description &&
      !stepRegisterOfficeState?.accountholderData?.identificationDocument?.isForeign
    ) {
      luogoRilascioDocumento =
        stepRegisterOfficeState?.accountholderData?.identificationDocument?.municipality
          ?.description;
    } else if (
      stepRegisterOfficeState?.accountholderData?.identificationDocument?.placeForeign &&
      stepRegisterOfficeState?.accountholderData?.identificationDocument?.isForeign
    ) {
      luogoRilascioDocumento =
        stepRegisterOfficeState?.accountholderData?.identificationDocument?.placeForeign;
    }

    let familyStepPerson = [] as Array<PracticeFamily>;
    let familyStepFamily = [] as Array<PracticeFamily>;

    if (formContext.values.family) {
      familyStepFamily = (formContext.values.family || []).map((item) => ({
        ...(item.subject as PracticeFamily),
        controlloCf: item.cfControl,
        // controllare se cittadinanza è un array
        cittadinanza:
          ((item?.subject as SubjectData)?.cittadinanza || [])?.length > 0
            ? ((item?.subject as SubjectData)?.cittadinanza || [])[0]
            : ((item?.subject as SubjectData)?.cittadinanza || [])?.length === 0
            ? undefined
            : (item.subject as PracticeFamily)?.cittadinanza || undefined,
        autoveicoli: {
          possessoAutoveicoli: item.auto,
          possessoPatente: item.drivingLicense,
        },
        generalita: {
          ...item.subject?.generalita,
          soggettoAIRE: item.aire,
        },
        legameSoggetto: {
          ...item.subject?.legameSoggetto,
          codiceLegame: item.relationship,
        },
        vecchioCodiceLegame: item.oldRelationship,
        selected: item.checked || item.selectedFromDb || false,
      })) as Array<PracticeFamily>;
    }

    if (formContext.values.person?.family) {
      familyStepPerson = (formContext.values.person?.family || []).map((item) => ({
        ...(item.subject as PracticeFamily),
        controlloCf: item.cfControl,
        // controllare se cittadinanza è un array
        cittadinanza:
          ((item?.subject as SubjectData)?.cittadinanza || [])?.length > 0
            ? ((item?.subject as SubjectData)?.cittadinanza || [])[0]
            : ((item?.subject as SubjectData)?.cittadinanza || [])?.length === 0
            ? undefined
            : (item.subject as PracticeFamily)?.cittadinanza || undefined,
        autoveicoli: {
          possessoAutoveicoli: item.auto,
          possessoPatente: item.drivingLicense,
        },
        generalita: {
          ...item.subject?.generalita,
          soggettoAIRE: item.aire,
        },
        legameSoggetto: {
          ...item.subject?.legameSoggetto,
          codiceLegame: item.relationship,
        },
        vecchioCodiceLegame: item.oldRelationship,
        selected: true,
      })) as Array<PracticeFamily>;
    }
    const dataToSend = {
      visualizzazioneSceltaFamigliaList: (familyStepPerson || []).concat(familyStepFamily || []),
      richiestaDichiarazioneDichiaranteVO: {
        codiceEnte: codeEnte || undefined,
        idAnnoPratica: yearId ? Number.parseInt(yearId, 10) : undefined,
        progressivoPratica: progressive ? Number.parseInt(progressive, 10) : undefined,
        tipoRegistro: typeRegister || undefined,
        dataRilascioDocumento: stepRegisterOfficeState?.applicantData?.identificationDocument
          ?.issuedDate
          ? stepRegisterOfficeState?.applicantData?.identificationDocument?.issuedDate.toFormat(
              'yyyy-MM-dd'
            )
          : stepRegisterOfficeState?.accountholderData?.identificationDocument?.issuedDate
          ? stepRegisterOfficeState?.accountholderData?.identificationDocument?.issuedDate.toFormat(
              'yyyy-MM-dd'
            )
          : undefined,
        luogoRilascioDocumento,
        numeroDocumento:
          stepRegisterOfficeState?.applicantData?.identificationDocument?.documentNumber ||
          stepRegisterOfficeState?.accountholderData?.identificationDocument?.documentNumber ||
          undefined,
      },
      tipoDatiRispostaEstrazioneIntestatarioVO: {
        generalita: transformGeneralityRequest(
          stepRegisterOfficeState?.accountholderData?.generality
        ),
        cittadinanza: transformCitizenship(stepRegisterOfficeState?.accountholderData?.citizenship),
        residenza: stepRegisterOfficeState?.accountholderData?.subjectData?.residenza,
      },
      tipoDatiRispostaEstrazioneRichiedenteVO: stepRegisterOfficeState?.applicantData?.subjectData
        ? stepRegisterOfficeState?.applicantData?.subjectData
        : stepRegisterOfficeState?.applicantData?.generality
        ? {
            generalita: transformGeneralityRequest(
              stepRegisterOfficeState?.applicantData?.generality
            ),
          }
        : undefined,
    };

    return (
      declarationDeclarant({
        data: dataToSend,
        responseType: 'blob',
        url: ForPracticeTemporary
          ? `${__PRACTICE_API_URL__}/pratiche/stampa/dichiarazione-anagrafe-temporanea`
          : `${__PRACTICE_API_URL__}/pratiche/stampa/dichiarazione-dichiarante`,
      }) as ResponsePromise<Blob>
    ).then((response) => {
      const pdfCertificate = response?.data;
      if (pdfCertificate) {
        const blob = new Blob([pdfCertificate], { type: 'application/pdf' });
        blobToBase64(blob).then((base64) => {
          setPdfModal({
            data: base64.split(',')[1],
            name: 'Riepilogo stampa',
            open: true,
          });
        });
      }
    });
  });

  return (
    <>
      <AccordionFamily
        formContext={formContext}
        idAnprIntestatario={idAnprIntestatario}
        disabledAll={disabledAll}
        disabled={disabled || ForPracticeTemporary}
        codeEnte={codeEnte}
        yearId={yearId}
        typeRegister={typeRegister}
        progressive={progressive}
        typePractice={typePractice}
        disabledResidente={isPersonaNonResidente ? false : idAnprIntestatario}
      />
      {isPraticeUnAvailability ? undefined : (
        <AccordionPerson
          formContext={formContext}
          ForPracticeTemporary={ForPracticeTemporary}
          codeEnte={codeEnte}
          yearId={yearId}
          typeRegister={typeRegister}
          progressive={progressive}
          typePractice={typePractice}
          idFamily={idFamily}
          idAnprIntestatario={isPersonaNonResidente ? false : idAnprIntestatario}
          isMaiAnagrafato={isMaiAnagrafato}
          disabled={disabled}
        />
      )}
      <div className="pmi-pt-3 pmi-d-flex pmi-justify-content-center">
        <div className="pmi-w-25 pmi-w-lg-15">
          <PmiButton variant="outlined" fullWidth color="cdmBrand" onClick={onBack}>
            Indietro
          </PmiButton>
        </div>
        {typeRegister === TypeRegister.Unavailable ? (
          <div className="pmi-pl-2 pmi-w-50">
            <PmiButton
              variant="outlined"
              fullWidth
              color="cdmBrandThird"
              onClick={printDeclarationDeclarant}
              disabled={disabledPrintDeclaration}
            >
              {'STAMPA DICHIARAZIONE DICHIARANTE'}
            </PmiButton>
          </div>
        ) : undefined}
        <PrintSuccessModalDeclarationDeclarant
          isOpen={isDeclarationSuccessModal}
          onConfirm={() => {
            formContext.handleSubmit();
            setDeclarationSuccessModal(false);
          }}
          rePrint={() => {
            printDeclarationDeclarant().then(() => {
              setDeclarationSuccessModal(false);
            });
          }}
          onClose={() => setDeclarationSuccessModal(false)}
        />
        <ModalViewerPDF
          open={pdfModal.open}
          data={pdfModal.data}
          name={pdfModal.name}
          title="Riepilogo Stampa"
          onClose={() => {
            setPdfModal({ data: '', open: false, name: '' });
            if (!ForPracticeTemporary) {
              setDeclarationSuccessModal(true);
            }
          }}
        />
        <div className="pmi-pl-2 pmi-w-25 pmi-w-lg-15">
          <PmiButton
            variant="outlined"
            fullWidth
            color="cdmBrandThird"
            onClick={() => {
              if (disabled) {
                onNext();

                return;
              }

              if (
                typeRegister === TypeRegister.Unavailable &&
                iniziativa === TypeInitiative.idPersonaLegittima
              ) {
                setDisabledPrintDeclaration(false);
                return;
              }

              if (data[0]?.tipoCasoPratica === ResidenceID.family) {
                setOpenModal(true);

                return;
              }

              formContext.handleSubmit();
            }}
          >
            Avanti
          </PmiButton>
        </div>
        <ModalDomesticPartnership
          open={openModal}
          onOkClose={() => {
            formContext.handleSubmit();
            setOpenModal(false);
          }}
          onCancel={() => setOpenModal(false)}
        />
        <ErrorModal
          open={errorModal.open}
          message={errorModal.message || ''}
          onClose={() =>
            setErrorModal({
              open: false,
            })
          }
        />
      </div>
    </>
  );
};

StepFamilyComponent.displayName = 'step component';

const StepFamily = memo(StepFamilyComponent) as typeof StepFamilyComponent;

export default StepFamily;
